'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface Slide {
  image: string;
  alt: string;
  badge: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  cta: string;
  ctaHref: string;
  secondaryCta: string;
  secondaryHref: string;
}

const slides: Slide[] = [
  {
    image: '/assets/images/hero_slider_doctor_consultation.png',
    alt: 'Médecin africaine en consultation avec un patient dans une clinique moderne COSA Abidjan',
    badge: 'Votre Santé, Notre Mission',
    title: 'Des soins d\'excellence',
    titleAccent: 'à votre portée',
    subtitle: 'COSA - Compagnie de Santé Africaine propose des consultations médicales de haute qualité dans un cadre moderne et accueillant en Côte d\'Ivoire.',
    cta: 'Prendre Rendez-vous',
    ctaHref: '#contact',
    secondaryCta: 'Découvrir nos Services',
    secondaryHref: '#services',
  },
  {
    image: '/assets/images/hero_slider_telemedicine.png',
    alt: 'Médecin africain utilisant une valise de télémédecine pour une consultation à distance',
    badge: 'Innovation Médicale',
    title: 'La télémédecine',
    titleAccent: 'au service de l\'Afrique',
    subtitle: 'Consultez un médecin qualifié depuis votre domicile grâce à notre technologie de télémédecine de pointe. L\'accès aux soins, partout et pour tous.',
    cta: 'Consulter en Ligne',
    ctaHref: '/services/telemedecine',
    secondaryCta: 'En Savoir Plus',
    secondaryHref: '/services/telemedecine',
  },
  {
    image: '/assets/images/hero_slider_medical_team.png',
    alt: 'Équipe médicale africaine professionnelle de COSA en blouses blanches dans un couloir de clinique moderne',
    badge: 'Notre Équipe',
    title: 'Des professionnels',
    titleAccent: 'dévoués à votre santé',
    subtitle: 'Une équipe pluridisciplinaire de médecins, spécialistes et paramédicaux formés aux meilleures pratiques internationales, à votre service chaque jour.',
    cta: 'Rencontrer l\'Équipe',
    ctaHref: '#apropos',
    secondaryCta: 'Nos Spécialités',
    secondaryHref: '#services',
  },
  {
    image: '/assets/images/hero_slider_pediatrics.png',
    alt: 'Pédiatre africain examinant un enfant avec sa mère dans une salle de consultation moderne',
    badge: 'Pédiatrie & Famille',
    title: 'La santé de vos enfants',
    titleAccent: 'entre de bonnes mains',
    subtitle: 'Notre service de pédiatrie offre un suivi médical complet pour vos enfants dans un environnement ludique et rassurant, conçu pour les plus jeunes.',
    cta: 'Consulter en Pédiatrie',
    ctaHref: '/services/medecine-specialisee',
    secondaryCta: 'Voir Nos Services',
    secondaryHref: '#services',
  },
  {
    image: '/assets/images/hero_slider_patient_care.png',
    alt: 'Infirmière africaine prenant la tension d\'un patient âgé dans une salle d\'examen moderne',
    badge: 'Soins Préventifs',
    title: 'Prévenir pour',
    titleAccent: 'mieux guérir',
    subtitle: 'Bilans annuels, vaccinations, programmes de prévention et suivi personnalisé. COSA vous accompagne tout au long de votre parcours de santé.',
    cta: 'Bilan de Santé',
    ctaHref: '/services/soins-preventifs',
    secondaryCta: 'Nos Programmes',
    secondaryHref: '/services/soins-preventifs',
  },
];

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const goTo = useCallback((index: number, dir: 'next' | 'prev' = 'next') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length, 'next');
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length, 'prev');
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  const css = `
    .hero-slider { position: relative; width: 100%; height: 100vh; min-height: 600px; overflow: hidden; }
    .hero-slide { position: absolute; inset: 0; transition: opacity 0.7s ease, transform 0.7s ease; }
    .hero-slide.active { opacity: 1; transform: scale(1); z-index: 2; }
    .hero-slide.inactive { opacity: 0; transform: scale(1.04); z-index: 1; }
    .hero-slide-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(14,89,50,0.88) 0%, rgba(14,89,50,0.65) 50%, rgba(14,89,50,0.25) 100%); z-index: 1; }
    .hero-content { position: relative; z-index: 3; height: 100%; display: flex; align-items: center; padding: 0 15px; }
    .hero-inner { max-width: 1300px; margin: 0 auto; width: 100%; padding-top: 80px; }
    .hero-badge { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: white; background: rgba(251,175,69,0.2); border: 1px solid rgba(251,175,69,0.4); border-radius: 100px; padding: 7px 16px; margin-bottom: 20px; }
    .hero-badge i { color: #fbaf45; font-size: 9px; }
    .hero-title { font-size: clamp(38px, 6vw, 76px); font-weight: 800; line-height: 1.1em; color: white; margin: 0 0 20px; font-family: 'Onest', sans-serif; }
    .hero-title-accent { color: #fbaf45; }
    .hero-subtitle { font-size: clamp(15px, 1.8vw, 18px); line-height: 1.75em; color: rgba(255,255,255,0.85); max-width: 580px; margin-bottom: 38px; }
    .hero-cta-group { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .hero-btn-primary { position: relative; display: inline-block; font-size: 15px; font-weight: 600; background: #fbaf45; color: white; border-radius: 100px; padding: 15px 28px; margin-right: 32px; border: none; transition: 0.4s ease-in-out; z-index: 1; overflow: visible; cursor: pointer; text-decoration: none; font-family: 'Onest', sans-serif; }
    .hero-btn-primary::before { content: ''; position: absolute; top: 50%; right: -32px; width: 46px; height: 46px; border-radius: 50%; background-color: #fbaf45; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12h14M13 6l6 6-6 6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: center; background-size: 20px; border: 2px solid rgba(14,89,50,0.3); transform: translateY(-50%); transition: 0.4s ease-in-out; }
    .hero-btn-primary:hover::before { background-color: #0e5932; transform: translateY(-50%) rotate(45deg); }
    .hero-btn-primary::after { content: ''; position: absolute; inset: 0 50%; width: 0; height: 100%; border-radius: 100px; opacity: 0; background: #0e5932; transition: 0.4s ease-in-out; z-index: -1; }
    .hero-btn-primary:hover::after { width: 100%; left: 0; right: 0; opacity: 1; }
    .hero-btn-secondary { display: flex; align-items: center; gap: 12px; cursor: pointer; text-decoration: none; }
    .hero-btn-secondary-circle { width: 48px; height: 48px; border-radius: 50%; background: rgba(255,255,255,0.15); border: 2px solid rgba(255,255,255,0.4); display: flex; align-items: center; justify-content: center; transition: all 0.3s; flex-shrink: 0; }
    .hero-btn-secondary:hover .hero-btn-secondary-circle { background: rgba(251,175,69,0.3); border-color: #fbaf45; }
    .hero-btn-secondary span { color: white; font-weight: 600; font-size: 15px; font-family: 'Onest', sans-serif; }
    .hero-nav-btn { position: absolute; top: 50%; transform: translateY(-50%); z-index: 10; width: 52px; height: 52px; border-radius: 50%; background: rgba(255,255,255,0.15); border: 2px solid rgba(255,255,255,0.3); color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.3s ease; backdrop-filter: blur(4px); }
    .hero-nav-btn:hover { background: #fbaf45; border-color: #fbaf45; transform: translateY(-50%) scale(1.1); }
    .hero-nav-prev { left: 24px; }
    .hero-nav-next { right: 24px; }
    .hero-dots { position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%); z-index: 10; display: flex; gap: 10px; align-items: center; }
    .hero-dot { width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.4); cursor: pointer; border: none; transition: all 0.3s ease; padding: 0; }
    .hero-dot.active { background: #fbaf45; width: 28px; border-radius: 5px; }
    .hero-progress { position: absolute; bottom: 0; left: 0; height: 3px; background: #fbaf45; z-index: 10; animation: heroProgress 6s linear infinite; }
    @keyframes heroProgress { from { width: 0%; } to { width: 100%; } }
    .hero-slide-counter { position: absolute; bottom: 32px; right: 32px; z-index: 10; font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.7); font-family: 'Onest', sans-serif; }
    .hero-slide-counter span { color: #fbaf45; font-size: 22px; }
    .hero-text-enter { animation: heroTextIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
    @keyframes heroTextIn { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
    @media (max-width: 768px) {
      .hero-nav-btn { width: 40px; height: 40px; font-size: 13px; }
      .hero-nav-prev { left: 12px; }
      .hero-nav-next { right: 12px; }
      .hero-slide-counter { display: none; }
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="hero-slider" id="accueil">
        {slides.map((s, i) => (
          <div key={i} className={`hero-slide ${i === current ? 'active' : 'inactive'}`}>
            <img src={s.image} alt={s.alt} className="hero-slide-img" />
            <div className="hero-overlay" />
          </div>
        ))}

        <div className="hero-content">
          <div className="hero-inner">
            <div style={{ maxWidth: 700 }}>
              <div key={`badge-${current}`} className="hero-text-enter" style={{ animationDelay: '0.05s' }}>
                <span className="hero-badge">
                  <i className="fa-solid fa-star-of-life"></i>
                  {slide.badge}
                </span>
              </div>
              <div key={`title-${current}`} className="hero-text-enter" style={{ animationDelay: '0.15s' }}>
                <h1 className="hero-title">
                  {slide.title}<br />
                  <span className="hero-title-accent">{slide.titleAccent}</span>
                </h1>
              </div>
              <div key={`sub-${current}`} className="hero-text-enter" style={{ animationDelay: '0.25s' }}>
                <p className="hero-subtitle">{slide.subtitle}</p>
              </div>
              <div key={`cta-${current}`} className="hero-text-enter hero-cta-group" style={{ animationDelay: '0.35s' }}>
                <Link href={slide.ctaHref} className="hero-btn-primary">{slide.cta}</Link>
                <Link href={slide.secondaryHref} className="hero-btn-secondary">
                  <div className="hero-btn-secondary-circle">
                    <i className="fa-solid fa-arrow-right" style={{ color: 'white', fontSize: 14 }}></i>
                  </div>
                  <span>{slide.secondaryCta}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <button className="hero-nav-btn hero-nav-prev" onClick={prev} aria-label="Diapositive précédente">
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <button className="hero-nav-btn hero-nav-next" onClick={next} aria-label="Diapositive suivante">
          <i className="fa-solid fa-chevron-right"></i>
        </button>

        {/* Dots */}
        <div className="hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i, i > current ? 'next' : 'prev')}
              aria-label={`Aller à la diapositive ${i + 1}`}
            />
          ))}
        </div>

        {/* Counter */}
        <div className="hero-slide-counter">
          <span>{String(current + 1).padStart(2, '0')}</span> / {String(slides.length).padStart(2, '0')}
        </div>

        {/* Progress bar */}
        <div key={current} className="hero-progress" />
      </div>
    </>
  );
}
